import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { inr } from "@/lib/api";
import ListingCard from "@/components/ListingCard";
import SafeImage from "@/components/SafeImage";
import MobileSearchSheet from "@/components/MobileSearchSheet";
import { SlidersHorizontal, X, Menu, ChevronLeft, ChevronRight, Loader2, Search, LayoutGrid, Rows3 } from "lucide-react";

// Per-page choices — multiples of 6 so the desktop grid always ends on a
// clean row. 60 is the default; 120 is the max the API allows.
const PER_PAGE_OPTIONS = [48, 60, 96, 120];
const MOBILE_BREAK = 640;

function useIsMobile() {
  const [mobile, setMobile] = useState(typeof window !== "undefined" && window.innerWidth < MOBILE_BREAK);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < MOBILE_BREAK);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return mobile;
}

export default function Marketplace() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // Mobile gallery mode: "grid" = the photo-first 3-col wall (default),
  // "cards" = 2-col cards with title, price and cart button.
  const [mobileView, setMobileView] = useState(() => localStorage.getItem("jx_mkt_view") || "grid");
  const switchView = (v) => { setMobileView(v); try { localStorage.setItem("jx_mkt_view", v); } catch { /* private mode */ } };
  const isMobile = useIsMobile();
  const sentinelRef = useRef();

  const category = params.get("category") || "";
  const q = params.get("q") || "";
  const sort = params.get("sort") || "newest";
  const fileType = params.get("file_type") || "";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10));
  const perPage = parseInt(params.get("per_page") || (isMobile ? "24" : "60"), 10);

  // Session-scoped shuffle seed — one seed per browser session, forwarded
  // to the backend so pagination stays consistent across pages/scrolls.
  // Regenerates the next time the user opens a fresh session.
  const shuffleSeed = React.useMemo(() => {
    try {
      let s = sessionStorage.getItem("marketplace_seed");
      if (!s) {
        s = String(Math.floor(Math.random() * 1_000_000_000));
        sessionStorage.setItem("marketplace_seed", s);
      }
      return s;
    } catch { return String(Math.floor(Math.random() * 1_000_000_000)); }
  }, []);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data.filter((c) => !c.parent_id)));
  }, []);

  // Fetch (paginated on desktop, infinite-scroll on mobile). The offset is
  // derived from `page` on desktop and from `items.length` on mobile so
  // scrolling appends the next window without changing the URL.
  const fetchPage = useCallback(async (opts = {}) => {
    const { append = false, offset = 0, limit } = opts;
    const isAppend = append;
    (isAppend ? setLoadingMore : setLoading)(true);
    try {
      // Only send the seed when the user hasn't picked an explicit sort —
      // otherwise the backend will fall back to exact ordering (spec §4).
      const useShuffle = !sort || sort === "newest";
      const r = await api.get("/listings", {
        params: {
          category_id: category || undefined,
          q: q || undefined,
          sort,
          file_type: fileType || undefined,
          limit: limit || perPage,
          offset,
          seed: useShuffle ? shuffleSeed : undefined,
        },
      });
      const payload = r.data;
      const rows = Array.isArray(payload) ? payload : (payload.items || []);
      const t = Array.isArray(payload) ? rows.length : (payload.total ?? rows.length);
      setTotal(t);
      setItems((prev) => (isAppend ? [...prev, ...rows] : rows));
    } finally {
      (isAppend ? setLoadingMore : setLoading)(false);
    }
  }, [category, q, sort, fileType, perPage]);

  // Reset + refetch when the filter / query / sort changes. Also fires on
  // per-page change on desktop.
  useEffect(() => {
    if (isMobile) {
      fetchPage({ offset: 0, limit: 24 });
    } else {
      fetchPage({ offset: (page - 1) * perPage });
      // Scroll to the top of the grid on every page flip so the buyer
      // doesn't land mid-scroll on page 3.
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    // eslint-disable-next-line
  }, [category, q, sort, fileType, page, perPage, isMobile]);

  // Infinite scroll — mobile only. Stops when we've loaded `total` items.
  useEffect(() => {
    if (!isMobile) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loadingMore && !loading && items.length < total) {
        fetchPage({ append: true, offset: items.length, limit: 24 });
      }
    }, { rootMargin: "600px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [isMobile, items.length, total, loading, loadingMore, fetchPage]);

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    // Reset page whenever a filter changes
    if (k !== "page") p.delete("page");
    setParams(p);
  };

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="max-w-[1600px] mx-auto px-3 sm:px-5 py-4 sm:py-8">
      {/* Mobile search — tapping opens the full-screen search sheet. */}
      <button
        onClick={() => setSearchOpen(true)}
        data-testid="marketplace-mobile-search"
        className="md:hidden w-full flex items-center gap-2 bg-secondary/60 border border-border rounded-full px-4 py-2.5 mb-4 text-sm text-muted-foreground text-left"
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="truncate">{q ? q : "Search designs, categories, designers…"}</span>
      </button>
      <MobileSearchSheet open={searchOpen} onClose={() => setSearchOpen(false)} />

      <div className="flex items-end justify-between mb-4 sm:mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setFilterOpen(true)}
            data-testid="filter-hamburger"
            className="flex items-center gap-2 border border-border rounded-md px-3 py-2 text-sm hover:border-primary hover:text-primary"
            aria-label="Open filters"
          >
            <Menu className="h-4 w-4" />
            <span className="hidden sm:inline font-medium">Filters</span>
          </button>
          <div>
            <h1 className="font-display font-bold text-xl sm:text-3xl leading-tight">Marketplace</h1>
            <p className="text-xs sm:text-sm text-muted-foreground font-mono mt-0.5" data-testid="marketplace-count">{total} production-ready files</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Mobile gallery-mode toggle: photo wall vs. cards with prices */}
          <div className="md:hidden flex border border-border rounded-md overflow-hidden" data-testid="mobile-view-toggle">
            <button
              onClick={() => switchView("grid")}
              data-testid="mobile-view-grid"
              aria-label="Photo grid"
              className={`h-10 w-10 flex items-center justify-center ${mobileView === "grid" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              onClick={() => switchView("cards")}
              data-testid="mobile-view-cards"
              aria-label="Cards with prices"
              className={`h-10 w-10 flex items-center justify-center ${mobileView === "cards" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
            >
              <Rows3 className="h-4 w-4" />
            </button>
          </div>
          <select
            data-testid="sort-select"
            value={sort}
            onChange={(e) => setParam("sort", e.target.value)}
            className="bg-card border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary"
          >
            <option value="newest">Newest</option>
            <option value="trending">Trending</option>
            <option value="bestselling">Best selling</option>
            <option value="price_low">Price: Low to High</option>
            <option value="price_high">Price: High to Low</option>
          </select>
        </div>
      </div>

      {/* Slide-out filter panel */}
      {filterOpen && (
        <div className="fixed inset-0 z-[9998] bg-slate-950/70" onClick={() => setFilterOpen(false)}>
          <aside
            className="absolute left-0 top-0 h-full w-[80%] max-w-[320px] bg-card border-r border-border p-5 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="filter-panel"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-sm font-semibold"><SlidersHorizontal className="h-4 w-4" /> Filters</div>
              <button onClick={() => setFilterOpen(false)} data-testid="filter-panel-close" className="p-1 hover:bg-secondary rounded"><X className="h-4 w-4" /></button>
            </div>
            {(category || q || fileType) && (
              <button onClick={() => { setParams({}); setFilterOpen(false); }} className="text-xs text-primary flex items-center gap-1 mb-4">
                <X className="h-3 w-3" /> Clear all
              </button>
            )}
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">File format</p>
            <div className="flex gap-2 mb-5">
              {["", "3dm", "stl"].map((ft) => (
                <button
                  key={ft || "all"}
                  data-testid={`filter-format-${ft || "all"}`}
                  onClick={() => setParam("file_type", ft)}
                  className={`font-mono text-[11px] uppercase px-3 py-1.5 rounded border font-bold ${fileType === ft ? "border-primary text-primary-foreground bg-primary" : "border-border text-muted-foreground"}`}
                >
                  {ft === "" ? "All" : ft}
                </button>
              ))}
            </div>
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Category</p>
            <div className="space-y-1">
              <button onClick={() => { setParam("category", ""); setFilterOpen(false); }} className={`block w-full text-left text-sm px-2 py-1.5 rounded hover:bg-secondary ${!category ? "text-primary font-medium bg-primary/5" : ""}`}>All categories</button>
              {cats.map((c) => (
                <button
                  key={c.id}
                  data-testid={`filter-cat-${c.id}`}
                  onClick={() => { setParam("category", c.id); setFilterOpen(false); }}
                  className={`block w-full text-left text-sm px-2 py-1.5 rounded hover:bg-secondary ${category === c.id ? "text-primary font-medium bg-primary/5" : ""}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </aside>
        </div>
      )}

      {/* MOBILE: Instagram-style 3-col image-only grid, zero gap, infinite scroll */}
      {isMobile ? (
        <>
          {loading && items.length === 0 ? (
            <div className="grid grid-cols-3 gap-[2px]" data-testid="mobile-grid-loading">
              {Array.from({ length: 12 }).map((_, i) => <div key={i} className="aspect-square bg-secondary animate-pulse" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="py-24 text-center text-muted-foreground text-sm">No files found. Try adjusting filters.</div>
          ) : (
            <>
              <div className={mobileView === "cards" ? "grid grid-cols-2 gap-3" : "grid grid-cols-3 gap-[2px]"} data-testid="mobile-grid">
                {mobileView === "cards"
                  ? items.map((l) => <ListingCard key={l.id} listing={l} />)
                  : items.map((l) => (
                  <Link
                    key={l.id}
                    to={`/design/${l.id}`}
                    className="relative aspect-square bg-slate-900 overflow-hidden"
                    data-testid={`mobile-tile-${l.id}`}
                  >
                    <SafeImage
                      src={l.cover_image ? `${l.cover_image}${l.cover_image.includes("?") ? "&" : "?"}w=400` : null}
                      alt={l.title}
                      className="w-full h-full object-cover"
                      testid={`mobile-tile-img-${l.id}`}
                    />
                    {l.product_id && (
                      <span className="absolute top-1 right-1 bg-black/70 text-white text-[9px] font-mono font-bold px-1.5 py-0.5 rounded pointer-events-none">
                        {l.product_id}
                      </span>
                    )}
                    {/* Price chip — buyers could not compare prices without
                        opening every single tile. */}
                    <span
                      className="absolute bottom-1 left-1 bg-black/75 text-white text-[10px] font-bold px-1.5 py-0.5 rounded pointer-events-none"
                      data-testid={`mobile-tile-price-${l.id}`}
                    >
                      {inr(l.price)}
                    </span>
                  </Link>
                ))}
              </div>
              {items.length < total && (
                <div ref={sentinelRef} className="py-8 flex justify-center text-muted-foreground" data-testid="mobile-infinite-sentinel">
                  {loadingMore && <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading more…</>}
                </div>
              )}
            </>
          )}
        </>
      ) : (
        // DESKTOP: 6-col grid with tighter card sizing
        <>
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
              {Array.from({ length: 12 }).map((_, i) => <div key={i} className="aspect-[3/4] rounded-md bg-secondary animate-pulse" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="border border-dashed border-border rounded-md py-24 text-center text-muted-foreground">No files found. Try adjusting filters.</div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3" data-testid="desktop-grid">
                {items.map((l) => <ListingCard key={l.id} listing={l} />)}
              </div>
              {/* Pagination bar — desktop only */}
              <div className="mt-6 flex flex-wrap items-center gap-4 justify-between border-t border-border pt-4" data-testid="pagination-bar">
                <div className="text-sm font-mono text-muted-foreground">
                  <b>{total.toLocaleString()}</b> items
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setParam("page", "1")}
                    disabled={page <= 1}
                    className="w-8 h-8 border border-border rounded flex items-center justify-center text-xs font-mono disabled:opacity-40 hover:border-primary"
                    data-testid="page-first"
                  >1</button>
                  <button
                    onClick={() => setParam("page", String(page - 1))}
                    disabled={page <= 1}
                    className="w-9 h-9 border border-border rounded flex items-center justify-center disabled:opacity-40 hover:border-primary"
                    data-testid="page-prev"
                  ><ChevronLeft className="h-4 w-4" /></button>
                  <input
                    type="number"
                    min={1}
                    max={totalPages}
                    value={page}
                    onChange={(e) => {
                      const n = Math.min(totalPages, Math.max(1, parseInt(e.target.value || "1", 10)));
                      setParam("page", String(n));
                    }}
                    className="w-14 h-9 border border-border rounded text-center text-sm font-mono focus:outline-none focus:border-primary bg-background"
                    data-testid="page-input"
                  />
                  <button
                    onClick={() => setParam("page", String(page + 1))}
                    disabled={page >= totalPages}
                    className="w-9 h-9 rounded flex items-center justify-center text-white bg-primary disabled:opacity-40 hover:bg-primary/90"
                    data-testid="page-next"
                  ><ChevronRight className="h-4 w-4" /></button>
                  <span className="text-xs font-mono text-muted-foreground ml-1" data-testid="page-total">
                    of {totalPages}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  Per page:
                  {PER_PAGE_OPTIONS.map((n) => (
                    <button
                      key={n}
                      onClick={() => setParam("per_page", String(n))}
                      data-testid={`per-page-${n}`}
                      className={`w-10 h-8 border rounded font-mono text-xs ${perPage === n ? "border-primary text-primary-foreground bg-primary font-bold" : "border-border hover:border-primary"}`}
                    >{n}</button>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
